import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export function useAwardFollows() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['award-follows', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('award_follows')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });
}

export function useFollowAward() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prizeName: string) => {
      if (!user) throw new Error('Must be logged in');

      const { data, error } = await supabase
        .from('award_follows')
        .insert({ user_id: user.id, prize_name: prizeName })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['award-follows'] });
      toast.success('Following award! You\'ll be notified when the shortlist drops.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to follow award');
    },
  });
}

export function useUnfollowAward() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prizeName: string) => {
      if (!user) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('award_follows')
        .delete()
        .eq('user_id', user.id)
        .eq('prize_name', prizeName);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['award-follows'] });
      toast.success('Unfollowed award');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to unfollow award');
    },
  });
}
